/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Component } from '@/components/region/component';
import { ComponentDataProvider, useComponentData, type ComponentType } from '@/components/region';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import { NavLink } from '@/components/link';
import { useLegacyAwareNavigate } from '@/hooks/use-legacy-aware-navigate';
import {
    type CarouselApi,
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@/components/ui/carousel';
import { useModalContext } from '@/contexts/modal-context';

/**
 * Mega-menu components are all `markupContent` (a raw HTML string), so they are
 * classified into layout zones by the BEM classes their markup contains:
 *
 *   - `subcategory__promotion`        → bottom promo bar
 *   - `subcategory-banner`            → banner (1 = static, 2+ = carousel)
 *   - `subcategories__cols__wrapper`  → the Men/Women 6-column block (middle)
 *   - else (`subcategories__col`)     → left nav (New & Featured / Explore / "Shop X")
 */
function markupOf(component: ComponentType): string {
    const data = component.data as Record<string, unknown> | undefined;
    return typeof data?.markup === 'string' ? data.markup : '';
}

export function classifyComponents(components: ComponentType[]) {
    const left: ComponentType[] = [];
    const middle: ComponentType[] = [];
    const banners: ComponentType[] = [];
    let promo: ComponentType | undefined;
    for (const component of components) {
        const m = markupOf(component);
        // Order matters: the 6-column block wins over "banner" so a stray
        // `subcategory-banner` string in its markup (e.g. a commented-out banner)
        // can't misclassify it.
        if (m.includes('subcategory__promotion')) promo = component;
        else if (m.includes('subcategories__cols__wrapper')) middle.push(component);
        else if (m.includes('subcategory-banner')) banners.push(component);
        else left.push(component);
    }
    return { left, middle, banners, promo };
}

interface NavMenuPageProps {
    page: PageWithComponentData;
    /** Top-level category name, shown as the bottom-left heading (e.g. "MEN"). */
    categoryName?: string;
    /** Top-level category id, used for the bottom-left "View All" link. */
    categoryId?: string;
    /** Top-nav image URL — when set, replaces the category name text in the promo bar. */
    categoryImage?: string;
    /** Fallback image shown in the promo bar when categoryImage is absent (e.g. MyJoys logo for Design My Own). */
    fallbackImage?: string;
    /** Called after a soft navigation so the parent can close the mega-menu panel. */
    onClose?: () => void;
}

/**
 * Production parity styles for the Page Designer mega-menu markup.
 *
 * The PD content is legacy SFRA markup using BEM classes (`.subcategories__*`,
 * `.subcategory-banner__*`, `.level-2__*`). storefront-next does not ship the
 * live site's stylesheet, so these rules port it. They are rendered inline via a
 * <style> element (rather than a theme CSS file) so they ship with the component
 * JS — which hot-reloads reliably — and are scoped under `.fj-mega-menu` /
 * `.fj-mega-banner` so they can never leak onto category pages.
 *
 * EXACT = from production computed styles. RECONSTRUCTED = from the screenshot.
 */
const MEGA_MENU_CSS = `
/* Subcategory columns — fixed 71px width + 15px padding-right = 86px per column. */

.fj-mega-menu .subcategories__cols__wrapper {
    display: flex !important;
    width: 100%;
    min-width: 0;
    padding-left: 40px;
     gap: 8.5px;
}

.fj-mega-menu .subcategories__col[data-col="accessories"] {
    min-width: 130px;
}

.fj-mega-middle {
    min-width: 0; 
}
 
.fj-mega-menu .subcategories__col {
    flex: 1 1 0;
}

.fj-mega-menu .subcategories__col {
    flex: 1 1 90px;
    min-width: 90px;
    
}

.fj-mega-menu .subcategories__list__item__link,
.fj-mega-menu .subcategories__col li {
    word-break: normal;
    overflow-wrap: break-word;
}

.fj-mega-left-wrapper--with-middle {
    width: 230px;
    flex-shrink: 0;
}

.fj-mega-left-wrapper--no-middle {
    width: 357.3px;
    flex-shrink: 0;
    display: flex;
}

.fj-mega-left--with-divider {
    position: relative;
    padding-right: 20px;
}
/* The vertical divider is drawn once, by the border-left on
   .subcategories__cols__wrapper--with-border (full column height).
   Do not add a second line here — it renders as a double divider. */

.fj-mega-left {  
   flex-shrink: 0;
    padding-right: 20px;
}

.fj-mega-left-inner {
    width: 210px;        
}

.fj-mega-menu {
    /* Must match paddingLeft of the dropdown content container in index.tsx
       (propsContentContainerStable). The menu pulls itself back to the left
       viewport edge and re-pads its own content, so the promo bar's top
       divider runs edge-to-edge while the columns stay where they are. */
    --fj-mega-inset: 76px;
    display: flex;
    flex-direction: column;
    max-height: min(700px, calc(100vh - var(--header-height, 106px)));
    margin-left: calc(-1 * var(--fj-mega-inset));
    width: calc(100% + var(--fj-mega-inset));
    padding-left: var(--fj-mega-inset);
}

.fj-mega-menu > .flex.w-full.flex-1.min-h-0 {
    overflow: hidden;
}

.fj-mega-menu > div {
    min-height: 0;
}

.fj-mega-left:empty {
    display: none;
}

.fj-mega-menu .flex.flex-1.min-h-0 > div:first-child {
    display: flex;
}

.fj-mega-menu .subcategories__cols__wrapper--with-border {
    border-left: 1px solid #c4c4c4;
    padding-left: 40px;
}
/* Within a column, stacked .subcategories blocks are 35px apart (EXACT). */
.fj-mega-menu .subcategories__col .subcategories + .subcategories {
    margin-top: 35px;
}

.fj-mega-menu .subcategories__wrapper,
.fj-mega-menu .subcategories__cols__wrapper {
    width: 100%;
}
   
.fj-mega-menu .subcategories__col li {
    white-space: normal;
}

.fj-mega-menu .subcategories__list__item__link {
    white-space: normal;
    overflow-wrap: break-word;
     display: block;
}

/* Section / column headings — "New & Featured", "Shoes"… EXACT
   (22px / 700 / #333, letter-spacing 0.275px, margin-bottom 10px). */
.fj-mega-menu .subcategories__name,
.fj-mega-menu .subcategories__name--no-link {
    margin: 0 0 10px;
    padding: 0;
    color: #333;
    font-family: var(--font-sans);
    font-weight: 700;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0.275px;
    text-transform: none;
    
}

.fj-mega-menu .subcategories__name + .subcategories__list {
    margin-top: 0 !important;
    padding-top: 0 !important;
}

.fj-mega-menu .subcategories__col .subcategories__list:first-of-type {
    margin-bottom: 35px !important;
}

/* "Explore" (no-link heading) carries a slightly larger gap. EXACT. */
.fj-mega-menu .subcategories__name--no-link {
    margin-bottom: 15px;
    margin-top:35px
}
    
.fj-mega-menu .subcategories__name {
    white-space: normal;
    word-break: break-word;
}

.fj-mega-menu {   
  overflow-x: hidden;   
  overflow-y: hidden;
}

.fj-mega-menu .subcategories__name__link {
    display: block;
    color: #1c1e29;
    font-weight: 700;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0.275px;
    text-transform: none;
    text-decoration: none;
}
.fj-mega-menu .subcategories__name__link:hover {
    text-decoration: underline;
}

/* Sub-heading — "Best Sellers", "By Width"… EXACT
   (16px / 700, line-height 30px, margin-top 26px). */
.fj-mega-menu .subcategories__sub-name {
    margin: 26px 0 0;
    padding: 0;
    color: var(--black);
    font-family: var(--font-sans);
    font-weight: 700;
    font-size: 16px;
    line-height: 30px;
    text-transform: none;
}

/* Link lists. RECONSTRUCTED spacing. */
.fj-mega-menu .subcategories__list {
    list-style: none;
    margin: 0;
    padding: 0;
}

/* Left-nav list links (New & Featured, Discover, "Shop Gloves/Gifts" lists):
   16px / 400 / #1c1e29, flex so an icon/badge and text sit on one line with a
   16px gap. Underline on hover only. EXACT. */
.fj-mega-menu .subcategories__list__item__link {
    
 display: block;
    color: #1c1e29;
    font-family: var(--font-sans);
    font-weight: 400;
    font-size: 16px;
    line-height: 30px;
    text-decoration: none;

}
    
.fj-mega-left .subcategories__list__item__link {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.fj-mega-menu .subcategories__cols__wrapper
.subcategories__list__item__link {
    display: block;
    white-space: normal;
    overflow-wrap: break-word;
}
   
.fj-mega-menu .subcategories__list__item__link span {
    display: inline-block;
}

/* "New" badge — outlined box beside link text */
.fj-mega-menu .badge-outlined {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: fit-content;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1.5px solid #1c1e29;
    color: #1c1e29;
    background: transparent;
    line-height: 1;
    vertical-align: middle;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
    flex: 0 0 auto;
    white-space: nowrap;
}

/* Badge inside a link: invert on hover */
.fj-mega-menu .subcategories__list__item__link:hover .badge-outlined {
    background: #1c1e29;
    color: #ffffff;
    border-color: #1c1e29;
    text-decoration: none;
}

/* Badge sitting alongside a link (sibling, not inside it): invert when the row is hovered */
.fj-mega-menu .subcategories__list__item:has(.subcategories__list__item__link:hover) .badge-outlined {
    background: #1c1e29;
    color: #ffffff;
    border-color: #1c1e29;
}

.fj-mega-menu .subcategories__list__item__link:hover {
    text-decoration: underline;
}
/* Icon (Find My Shoe / Explore logos) keeps its authored inline max-width and
   sits beside the text via the flex gap above. */
.fj-mega-menu .subcategories__list__item__link img {
    flex: 0 0 auto;
}

/* Desktop-only: hide collapse toggles, "View All" overlay, and mobile-only links. */
.fj-mega-menu .subcategories__name__toggle-btn,
.fj-mega-menu .level-2__category__view-all__link,
.fj-mega-menu .display-mobile-only,
.fj-mega-menu .subcategories__list__item__link--mobile-only,
.fj-mega-menu .subcategories__list__item:has(.display-mobile-only),
.fj-mega-menu .subcategories__list__item:has(.subcategories__list__item__link--mobile-only) {
    display: none !important;
}

/* Promo banner — exact production dimensions: 410 × 550px. */
.fj-mega-banner {
    flex: 0 0 410px !important;
    width: 410px !important;
    max-width: 410px !important;
 max-height: 516px;
    overflow: hidden;
    position: relative;
}
/* Men/Women categories use a narrower 380px banner. */
.fj-mega-banner--narrow {
    flex: 0 0 380px !important;
    width: 380px !important;
    max-width: 380px !important;
}
.fj-mega-banner .subcategory-banner,
.fj-mega-banner .subcategory-banner__wrapper {  
 position: relative;
    width: 100%;
    aspect-ratio: 410 / 550;
    max-height: 550px;
    overflow: hidden;    
}
.fj-mega-banner .subcategory-banner__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 1;
}
    
.fj-mega-banner .subcategory-banner,
.fj-mega-banner .subcategory-banner__wrapper,
.fj-mega-banner .subcategory-banner__image {
    height: 100% !important;
    max-height: 516px !important;
}

.fj-mega-banner img,
.fj-mega-banner .subcategory-banner__image__img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
}
   
.fj-mega-banner .subcategory-banner__details {
    padding: 30px;
    bottom: 20px;
}

.fj-mega-banner .subcategory-banner__details {
    position: absolute;
    left: 0;
    bottom: 50px;
    width: 100%;
    color: #ffffff;
    z-index: 2;
}
.fj-mega-banner .subcategory-banner__product-name__link {
    display: inline-block;
    color: var(--white);
    font-weight: 700;
    font-size: 26px;
    line-height: 1.1;
    letter-spacing: 0.0125em;
    text-transform: uppercase;
    text-decoration: none;
}
.fj-mega-banner .subcategory-banner__product-name__link:hover {
    text-decoration: underline;
}
.fj-mega-banner .subcategory-banner__description {
    margin: 12px 0 16px;
    color: var(--white);
    font-size: 14px;
    line-height: 1.4;
}
.fj-mega-banner .subcategory-banner__cta {
    display: inline-block;
    padding: 10px 22px;
    border: 1px solid var(--white);
    color: var(--white);
    font-size: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    text-decoration: none;
}
.fj-mega-banner .subcategory-banner__cta:hover {
    background: #1c1e29;
    color: #fff;
    border-color: #1c1e29;
}

/* Short viewports (e.g. 100% browser zoom on a laptop): the menu is capped by
   viewport height, so pull the banner text up and tighten spacing to keep the
   CTA visible. Banner size is unchanged. */
@media (max-height: 900px) {
    /* Anchor the text block's bottom edge to the *visible* bottom of the banner:
       menu height (capped like .fj-mega-menu) minus the 72px promo bar, capped
       at the banner's own 516px height, minus a 24px gap. */
    .fj-mega-banner .subcategory-banner__details {
        top: calc(min(516px, min(700px, 100vh - var(--header-height, 106px)) - 80px) - 24px);
        bottom: auto;
        transform: translateY(-100%);
        padding: 0 30px;
    }
    .fj-mega-banner .subcategory-banner__product-name__link {
        font-size: 24px;
    }
    .fj-mega-banner .subcategory-banner__description {
        margin: 6px 0 10px;
        font-size: 13px;
        line-height: 1.3;
    }
    .fj-mega-banner .subcategory-banner__cta {
        padding: 8px 20px;
    }
}

/* FJ Insider card (left sidebar, left-nav): "Join Us Inside The Ropes" with logo, heading,
   description, and Sign In / Sign Up buttons. */
.fj-mega-menu .fj-insider-card {
    margin-bottom: 30px;
}
.fj-mega-menu .fj-insider-logo {
    margin-bottom: 12px;
    height: auto;
    width: auto;
    max-width: 100%;
}
.fj-mega-menu .fj-insider-heading {
    font-family: var(--font-sans);
    font-size: 24px;
    font-weight: 700;
    line-height: 29px;
    color: #1c1e29;
    margin: 0 0 16px;
    padding-top: 20px;
    letter-spacing: normal;
}
.fj-mega-menu .fj-insider-description {
    font-family: var(--font-sans);
    font-size: 16px;
    font-weight: 400;
    line-height: 19px;
    color: #1c1e29;
    margin-bottom: 16px;
}
.fj-mega-menu .fj-insider-buttons {
    display: flex;
    gap: 8px;
}
.fj-mega-menu .fj-insider-button {
    flex: 1;
    padding: 10px 16px;
    border: 0.666667px solid #1c1e29;
    border-radius: 3px;
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 700;
    line-height: 17px;
    letter-spacing: 0.175px;
    text-transform: uppercase;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.5s ease;
}
.fj-mega-menu .fj-insider-button.signin {
    background-color: var(--white);
    color: #1c1e29;
}
.fj-mega-menu .fj-insider-button.signin:hover {
    background-color: #f5f5f5;
}
.fj-mega-menu .fj-insider-button.signup {
    background-color: #1c1e29;
    color: var(--white);
}
.fj-mega-menu .fj-insider-button.signup:hover {
    background-color: #333;
}

/* FJ Insider card — left nav (Fan Shop, What's New). Shared header__insider__ classes. */
.fj-mega-menu .header__insider img {
    max-width: 125px;
    margin-left: -5px;
    margin-top:35px;
}
.fj-mega-menu .header__insider__title {
    font-family: var(--font-sans);
    font-size: 24px;
    font-weight: 700;
    line-height: 29px;
    margin: 0 0 16px;
    text-transform: none;
}
.fj-mega-menu .header__insider__description {
    font-family: var(--font-sans);
    font-size: 16px;
    font-weight: 400;
    letter-spacing: normal;
    line-height: 19px;
    margin-bottom: 16px;
}
.fj-mega-menu .header__insider__buttons {
    display: flex;
    gap: 10px;
}
.fj-mega-menu .header__insider__buttons__button {
    padding: 10px 16px;
    height: auto;
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 700;
    line-height: 17px;
    letter-spacing: 0.0125em;
    color: #1c1e29;
    background: var(--white);
    border: 1px solid;
    border-radius: 3px;
    text-decoration: none;
    text-align: center;
    cursor: pointer;
}
.fj-mega-menu .header__insider__buttons__button:hover {
    background: #1c1e29;
    color: var(--white);
}
.fj-mega-menu .header__insider__buttons__button:last-child {
    background: #1c1e29;
    color: var(--white);
}
.fj-mega-menu .header__insider__buttons__button:last-child:hover {
    background: var(--white);
    color: #1c1e29;
}

/* Stay Dialed In / SMS signup button — left nav of "What's New" mega menu. */
.fj-mega-menu .header__insider__buttons a.cta-button {
    display: inline-block;
    font-family: var(--font-sans);
    font-size: 16px;
    font-weight: 700;
    letter-spacing: normal;
    line-height: 2rem;
    height: 43.33px;
    padding: 5px 14px;
    text-transform: uppercase;
    text-decoration: none;
    border: 1px solid var(--black);
    background-color: transparent;
    color: var(--black);
    transition: background-color 0.5s ease, color 0.5s ease;
    cursor: pointer;
    text-align: center;
}
.fj-mega-menu .header__insider__buttons a.cta-button:hover {
    background-color: transparent;
}
.fj-mega-menu .header__insider__buttons a.cta-button {
    margin-bottom: 20px;
}

.fj-mega-carousel {
    overflow: hidden;
    display: block;
}

.fj-mega-carousel .embla__viewport {
    overflow: hidden;
    display: block;
    transform: none !important;
    margin-left: 0 !important;
    padding-left: 0 !important;
}

.fj-mega-carousel .embla__container {
    display: flex;
    align-items: stretch;
    justify-content: flex-start !important;
    transform: none !important;
    margin-left: 0 !important;
}

.fj-mega-carousel .fj-mega-banner {
    flex: 0 0 410px !important;
    min-width: 410px !important;
    max-width: 410px !important;
    margin-right: 16px;
}

.fj-mega-carousel,
.fj-mega-carousel * {
    margin-left: 0 !important;
}

.fj-mega-carousel .embla__container > * {
    flex-grow: 0 !important;
}

/* Banner carousel (categories with multiple banners — Gloves, Gear, etc.). */
.fj-mega-banners,
.fj-mega-carousel,
.fj-mega-carousel > div {
    position: relative;
    height: 100%;
     overflow: hidden;
}
     
.fj-mega-arrow:disabled {
    display: none !important;
}

/* Each slide is a fixed 410px banner; arrows page through the rest. */
.fj-mega-carousel .fj-mega-banner {
    flex: 0 0 410px !important;
    min-width: 410px !important;
    width: 410px !important;
    max-width: 410px !important;
    margin-right: 16px;
}
.fj-mega-carousel .fj-mega-banner--narrow {
    flex: 0 0 380px !important;
    min-width: 380px !important;
    width: 380px !important;
    max-width: 380px !important;
}
/* Static row (all banners fit): fill available space proportionally, max 410px each. */
.fj-mega-banners-row {
    height: 100%;
    gap: 16px;
}
.fj-mega-banners-row .fj-mega-banner {
    flex: 1 1 0 !important;
    width: auto !important;
    max-width: 410px !important;
    min-width: 0 !important;
}
.fj-mega-banners-row .fj-mega-banner--narrow {
    max-width: 380px !important;
}
/* Carousel arrows — square white box with CSS chevron. */
.fj-mega-arrow {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    width: 60px !important;
    height: 60px !important;
    background: var(--white) !important;
    border-radius: 3px !important;
    border: none !important;
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25) !important;
    opacity: 1 !important;
    z-index: 10 !important;
    cursor: pointer;
}
/* Hide default Lucide icon rendered by CarouselPrevious/Next */
.fj-mega-arrow svg {
    display: none !important;
}
/* CSS chevron drawn with two borders */
.fj-mega-arrow::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 14px;
    height: 14px;
    border-top: 4px solid #1c1e29;
    border-left: 4px solid #1c1e29;
}
.fj-mega-arrow--next {
    right: 20px !important;
    left: initial !important;
}
.fj-mega-arrow--next::after {
    transform: translate(-50%, -50%) rotate(135deg);
}
.fj-mega-arrow--prev {
    left: 20px !important;
    right: auto !important;
}
.fj-mega-arrow--prev::after {
    transform: translate(-50%, -50%) rotate(-45deg);
}

/* Bottom bar — "MEN  View All" (left) + "Free Shipping … LEARN MORE" (centered).
   The promo text is 16px / 700 / #1c1e29, centered. EXACT. */

.fj-mega-promo {
    position: relative;
    background: white;
    z-index: 10;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 72px;
    min-height: 72px;
    /* Break out of the menu's left inset so the top border spans the full width. */
    margin-left: calc(-1 * var(--fj-mega-inset, 0px));
    /* Keep the centered promo text where it was before the bar was widened. */
    padding-left: var(--fj-mega-inset, 0px);
}

/* Responsive: at 150% display scale (~1280px CSS viewport) tighten inner padding
   so the 5-column grid and left nav are not cramped. */
@media (max-width: 1380px) {
    .fj-mega-menu .subcategories__cols__wrapper {
        padding-left: 40px !important;
    }
    .fj-mega-menu .subcategories__cols__wrapper--with-border {
        padding-left: 40px !important;
    }
    .fj-mega-menu .subcategories__name,
    .fj-mega-menu .subcategories__name--no-link {
        font-size: 22px;
        line-height: 24px;
    }
    .fj-mega-menu .subcategories__name__link {
        font-size: 22px;
        line-height: 24px;
    }
    .fj-mega-left-wrapper--with-middle {
        width: 200px;
    }
    .fj-mega-left-inner {
        width: 210px;
    }
}
   
.fj-mega-banner {
    flex-shrink: 0;
    overflow: hidden;
}
 
/* "MEN  View All" sits on the left; the promo text is centered across the bar. */
.fj-mega-promo__left {
    
position: absolute;
    left: var(--fj-mega-inset, 0px);
    display: flex;
    align-items: center;
    white-space: nowrap

}
.fj-mega-promo__text {
    text-align: center;
}
    
.fj-mega-content {
    overflow: hidden;
     min-height: 0;
}

/* Promo text — "Free Shipping…" 16px / 700 / #1c1e29, centered. EXACT. */
.fj-mega-promo .subcategory__promotion,
.fj-mega-promo__text {
    margin: 0;
    color: #1c1e29;
    font-family: var(--font-sans);
    font-size: 16px;
    font-weight: 700;
    line-height: 36px;
    letter-spacing: normal;
    text-align: center;
    text-transform: none;
}
/* "LEARN MORE" link — 16px / 400 / underline, normal case (not bold). EXACT. */
.fj-mega-promo .subcategory__promotion__link,
.fj-mega-promo__text a {
    color: #1c1e29;
    font-weight: 400;
    font-size: 16px;
    text-decoration: underline;
    text-transform: none;
}
.fj-mega-promo .subcategory__promotion__link:hover,
.fj-mega-promo__text a:hover {
    text-decoration: none;
}
/* Category name — "MEN" (36px / 700 / uppercase). EXACT. */
.fj-mega-promo__cat {
    display: inline-block;
    color: var(--black);
    font-family: var(--font-sans);
    font-size: 36px;
    font-weight: 700;
    line-height: 36px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
}
/* Category image (e.g. MyJoys logo) in place of the text heading. */
.fj-mega-promo__cat-img {
    display: inline-block;
    height: 36px;
    width: auto;
    object-fit: contain;
    vertical-align: middle;
}
/* "View All" link (16px / 400 / underline, 30px left margin). EXACT. */
.fj-mega-promo__view-all {
    display: inline-block;
    margin-left: 30px;
    color: #1c1e29;
    font-family: var(--font-sans);
    font-size: 16px;
    font-weight: 400;
    line-height: 36px;
    text-transform: none;
    text-decoration: underline;
}
.fj-mega-promo__view-all:hover {
    text-decoration: none;
}

.fj-mega-promo__text {
    margin-left: 50px;
}

/* Men/Women banner: fill the full content height so it meets the promo bar's
   divider line at any zoom level (the 516px cap left a gap when zoomed out). */
.fj-mega-banner.fj-mega-banner--narrow {
    align-self: stretch;
    min-height: 516px;
    max-height: none !important;
}
.fj-mega-banner--narrow .subcategory-banner,
.fj-mega-banner--narrow .subcategory-banner__wrapper {
    position: absolute !important;
    inset: 0;
    width: 100%;
    height: 100% !important;
    max-height: none !important;
    aspect-ratio: auto;
}
.fj-mega-banner--narrow .subcategory-banner__image {
    height: 100% !important;
    max-height: none !important;
}

@media (min-width: 1025px) and (max-width: 1250px) {
    .fj-mega-banner--narrow {
        display: none !important;
    }
}
`;

/**
 * Renders a Page Designer `global-menu-<categoryId>` page in a 3-column grid.
 *
 * The PD page is authored as a single content region with up to five components
 * in sequence. The components are mapped by index to the layout positions:
 *
 *   index 0 → left column, top    (e.g. "New & Featured")
 *   index 1 → left column, bottom (e.g. "Explore" / "Discover")
 *   index 2 → middle      (subcategories: Shoes / Apparel / Gloves / Junior / Accessories / Sale)
 *   index 3 → right       (promo banner, flush to the panel's right edge)
 *   index 4 → bottom      (promo bar / shipping text — spans all columns)
 *
 * The left column stacks [0] over [1] in a single cell so the thin vertical
 * divider (its right border) runs the exact height of that column, matching
 * production. The banner sits in a fixed-width right column with no right
 * padding so the image bleeds to the panel edge.
 *
 *   col 1 (260px)      col 2 (1fr)        col 3 (500px)
 *   ┌──────────┐│┌───────────────┐ ┌──────────────────┐  row 1
 *   │   [0]    ││  │               │ │                  │
 *   │          ││  │      [2]      │ │       [3]        │
 *   │   [1]    ││  │               │ │                  │
 *   ├──────────┴┴──┴───────────────┴─┴──────────────────┤  row 2
 *   │                       [4]                          │
 *   └────────────────────────────────────────────────────┘
 */
export function NavMenuPage({ page, categoryName, categoryId, categoryImage, fallbackImage, onClose }: NavMenuPageProps) {
    const { openInsiderModal, openSmsModal } = useModalContext();
    const navigate = useLegacyAwareNavigate();
    const containerRef = useRef<HTMLDivElement>(null);    
    const [isOverflowing, setIsOverflowing] = useState(true);
    const bannerRef = useRef<HTMLDivElement | null>(null);
    const [carouselApi, setCarouselApi] = useState<CarouselApi | undefined>();
    const [atCarouselStart, setAtCarouselStart] = useState(true);
    const [atCarouselEnd, setAtCarouselEnd] = useState(false);
    const [viewAllUrl, setViewAllUrl] = useState(() => categoryId ? `/category/${categoryId}` : '');

    useEffect(() => {
        if (!carouselApi) return;
        const update = () => {
            const progress = carouselApi.scrollProgress();
            const canPrev = carouselApi.canScrollPrev();
            const canNext = carouselApi.canScrollNext();
            setAtCarouselStart(progress <= 0.001 || !canPrev);
            setAtCarouselEnd(progress >= 0.999 || !canNext);
        };
        update();
        carouselApi.on('scroll', update);
        carouselApi.on('reInit', update);
        return () => {
            carouselApi.off('scroll', update);
            carouselApi.off('reInit', update);
        };
    }, [carouselApi]);

    const handleCarouselNext = useCallback(() => {
        if (!carouselApi) return;
        carouselApi.scrollTo(carouselApi.scrollSnapList().length - 1);
    }, [carouselApi]);

    const handleCarouselPrev = useCallback(() => {
        if (!carouselApi) return;
        carouselApi.scrollTo(0);
    }, [carouselApi]);

    // Attach click handlers for FJ Insider Sign In and SMS Sign Up buttons
    useEffect(() => {
        if (!containerRef.current) return;

        // Use a MutationObserver to handle dynamic PD markup rendering
        const attachHandlers = () => {
            let attached = false;

            // FJ Insider Sign In button (.header__insider__buttons__button:not([data-action]))
            const signInBtn = containerRef.current?.querySelector(
                '.header__insider__buttons__button:not([data-action])'
            );
            if (signInBtn && !signInBtn.hasAttribute('data-insider-signin-attached')) {
                const handleSignIn = (e: Event) => {
                    e.preventDefault();
                    openInsiderModal?.('login');
                };
                signInBtn.addEventListener('click', handleSignIn);
                signInBtn.setAttribute('data-insider-signin-attached', 'true');
                attached = true;
            }

            // SMS Sign Up button (a.cta-button for "Stay Dialed In" / "What's New" section)
            const smsBtn = containerRef.current?.querySelector('a.cta-button');
            if (smsBtn && !smsBtn.hasAttribute('data-sms-signup-attached')) {
                const handleSmsSignup = (e: Event) => {
                    e.preventDefault();
                    openSmsModal?.();
                };
                smsBtn.addEventListener('click', handleSmsSignup);
                smsBtn.setAttribute('data-sms-signup-attached', 'true');
                attached = true;
            }

            return attached;
        };

        if (attachHandlers()) return;

        const observer = new MutationObserver(() => {
            if (attachHandlers()) {
                observer.disconnect();
            }
        });

        observer.observe(containerRef.current, {
            childList: true,
            subtree: true,
            attributes: false,
        });

        return () => observer.disconnect();
    }, [openInsiderModal, openSmsModal]);

    // Stamp data-col on each subcategory column so CSS width rules can target
    // columns by content type regardless of what id the PD markup assigns.
    useEffect(() => {
        const stamp = () => {
            const cols = containerRef.current?.querySelectorAll(
                '.subcategories__cols__wrapper .subcategories__col'
            );
            cols?.forEach((col) => {
                if (col.hasAttribute('data-col')) return;
                const heading = col.querySelector('.subcategories__name, .subcategories__name--no-link');
                const text = heading?.textContent?.toLowerCase().trim() ?? '';
                if (text.includes('apparel')) col.setAttribute('data-col', 'apparel');
                else if (text.includes('glove')) col.setAttribute('data-col', 'gloves');
                else if (text.includes('accessor')) col.setAttribute('data-col', 'accessories');
                else if (text.includes('gear')) col.setAttribute('data-col', 'gear');
            });
        };

        stamp();
        const observer = new MutationObserver(stamp);
        if (containerRef.current) {
            observer.observe(containerRef.current, { childList: true, subtree: true });
        }
        return () => observer.disconnect();
    }, []);

    const region = page.regions?.[0];
    const components = (region?.components ?? []) as ComponentType[];
    if (!region || !components.length) return null;

    const regionId = region.id;
    const { left, middle, banners, promo } = classifyComponents(components);

    const isCarousel = banners.length > 1;
    // Men/Women have the 6-column middle; the other categories don't.
    const hasMiddle = middle.length > 0;
    // "View All" (and the wider banner treatment) is for Men/Women only.
    const showViewAll = hasMiddle;

    // Extract the PD-authored "View All" URL from the hidden .level-2__category__view-all__link
    // element in the markup, mirroring how the mobile component resolves it. The PD URL
    // is the authoritative destination; /category/${categoryId} is only the fallback.
    useEffect(() => {
        if (!showViewAll || !containerRef.current) return;
        const extract = () => {
            const pdLink = containerRef.current?.querySelector<HTMLAnchorElement>('.level-2__category__view-all__link');
            if (pdLink?.href) {
                const url = new URL(pdLink.href);
                setViewAllUrl(url.pathname + url.search);
            }
        };
        extract();
        const observer = new MutationObserver(extract);
        observer.observe(containerRef.current, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [showViewAll]);

    useEffect(() => {
       const check = () => {
       const el = bannerRef.current;
       if (!el) return;
       const containerWidth = el.offsetWidth;
       const totalWidth = banners.length * 350 + (banners.length - 1) * 16;

    setIsOverflowing(totalWidth > containerWidth);
  };

   check();
    window.addEventListener('resize', check);
   return () => window.removeEventListener('resize', check);
}, [banners.length]);

    // Intercept sub-category link clicks from PD-authored HTML and turn them
    // into soft SPA navigations instead of full page reloads.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleClick = (e: MouseEvent) => {
            // Match any anchor — not just .subcategories__list__item__link.
            // Bold section-heading links (SHOES, APPAREL) use a different CSS
            // class and were never caught by the old selector, causing full
            // page reloads instead of SPA navigation.
            const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
            if (!link || !container.contains(link)) return;

            // Preserve native link semantics: let the browser handle
            // open-in-new-tab (Cmd/Ctrl), middle-click, and modified clicks.
            if (
                e.defaultPrevented ||
                e.button !== 0 ||
                e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
            ) {
                return;
            }

            const href = link.getAttribute('href');
            if (!href) return;

            // Skip external links and new-tab targets — let the browser handle those.
            if (link.target === '_blank') return;
            let url: URL;
            try {
                url = new URL(href, window.location.origin);
            } catch {
                return; // unparseable href — leave to browser
            }
            // Do NOT check url.origin === window.location.origin. Authored PD links are
            // absolute URLs pointing to the deployed environment (e.g. aazw-012), which
            // has a different origin than localhost. Always strip the origin and navigate
            // by pathname only. Skip only non-navigational protocols (javascript:, mailto:, tel:).
            if (url.protocol !== 'https:' && url.protocol !== 'http:') return;

            // Authored hrefs are absolute URLs — strip origin so only
            // pathname + search is passed to navigate(), matching the same
            // normalization used for the "View All" link above.
            const path = url.pathname + url.search;

            e.preventDefault();
            void navigate(path);
            onClose?.();
        };

        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    }, [navigate, onClose]);

const grid = (
  <div
    ref={containerRef}
    className="fj-mega-menu flex w-full flex-col overflow-hidden">
    <style dangerouslySetInnerHTML={{ __html: MEGA_MENU_CSS }} />
    <div className="fj-mega-content flex w-full flex-1 min-h-0"
    style={{
    overflow: 'hidden'
  }}>
<div
  className="flex flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
  style={{
    width: hasMiddle ? 'calc(100% - 380px)' : '357.3px',
    flex: hasMiddle ? '1 1 auto' : '0 0 357.3px',
  }}>  
     {left.length > 0 && (  
<div
  className={`${
    hasMiddle ? 'w-[230px]' : 'w-[357.3px]'
  } shrink-0`}
  style={{ flex: hasMiddle ? undefined : '0 0 357.3px' }}>
            <div
              className={`fj-mega-left w-[230px] pt-[57px] flex flex-col ${
             hasMiddle ? 'fj-mega-left--with-divider' : ''
          }`} >
              <div className="w-[210px]">
                {left.map((c) => (
                  <Component key={c.id} component={c} regionId={regionId} />
                ))}
              </div>
            </div>
             
          </div>
        )}
        {hasMiddle && (
          <div className="flex-1 min-w-0 pt-[57px]">
            {middle.map((c) => (
              <Component key={c.id} component={c} regionId={regionId} />
            ))}
          </div>
        )}

      </div>
      {banners.length > 0 && (
        isCarousel ? (
          isOverflowing ? (
            // Banners overflow the available width → carousel with navigation arrows.
            <div
              ref={bannerRef}
              className="flex-1 min-w-0 flex justify-start">
              <Carousel
                className="fj-mega-carousel"
                opts={{ align: 'start', containScroll: 'trimSnaps' }}
                setApi={setCarouselApi}
              >
                <CarouselContent className="ml-0 h-full flex justify-start !translate-x-0">
                  {banners.map((c) => (
                    <CarouselItem key={c.id} className={`fj-mega-banner pl-0${hasMiddle ? ' fj-mega-banner--narrow' : ''}`}>
                      <Component component={c} regionId={regionId} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {!atCarouselStart && (
                  <button type="button" aria-label="Previous slide" className="fj-mega-arrow fj-mega-arrow--prev" onClick={handleCarouselPrev} />
                )}
                {!atCarouselEnd && (
                  <button type="button" aria-label="Next slide" className="fj-mega-arrow fj-mega-arrow--next" onClick={handleCarouselNext} />
                )}
              </Carousel>
            </div>
          ) : (
            // All banners fit without scrolling → static row aligned right, no carousel chrome.
            <div
              ref={bannerRef}
              className="flex-1 min-w-0 flex justify-end items-stretch fj-mega-banners-row">
              {banners.map((c) => (
                <div
                  key={c.id}
                  className={`fj-mega-banner${hasMiddle ? ' fj-mega-banner--narrow' : ''}`}>
                  <Component component={c} regionId={regionId} />
                </div>
              ))}
            </div>
          )
        ) : (
          <div className={`fj-mega-banner ${hasMiddle ? 'fj-mega-banner--narrow shrink-0' : 'flex-1'}`}>
            <Component component={banners[0]} regionId={regionId} />
          </div>
        )
      )}

    </div>

    {/* ✅ PROMO BAR (STICKY + NO SCROLL) */}
    {(promo || categoryName) && (
      <div className="fj-mega-promo shrink-0 border-t border-border py-4 bg-white">
        {(categoryName || categoryImage || fallbackImage) && (
          <div className="fj-mega-promo__left">
            {categoryImage ? (
              <img src={categoryImage} alt={categoryName ?? ''} className="fj-mega-promo__cat-img" />
            ) : fallbackImage ? (
              <img src={fallbackImage} alt={categoryName ?? ''} className="fj-mega-promo__cat-img" />
            ) : (
              <span className="fj-mega-promo__cat">{categoryName}</span>
            )}
            {showViewAll && viewAllUrl && (
              <NavLink to={viewAllUrl} className="fj-mega-promo__view-all">
                View All
              </NavLink>
            )}
          </div>
        )}

        {promo ? (
          <div className="fj-mega-promo__text">
            <Component component={promo} regionId={regionId} />
          </div>
        ) : (
          <p className="fj-mega-promo__text subcategory__promotion">
            Free Shipping on all orders over $150+{' '}
            <NavLink to="/shipping" className="subcategory__promotion__link">
              LEARN MORE
            </NavLink>
          </p>
        )}
      </div>
    )}
  </div>
);


 return <NavMenuPageDataProvider componentData={page.componentData}>{grid}</NavMenuPageDataProvider>;
}

/**
 * Wraps the grid with ComponentDataProvider only if the page carries componentData
 * and we aren't already inside an existing provider (e.g. rendered nested under
 * a parent page region).
 */
function NavMenuPageDataProvider({
    componentData,
    children,
}: {
    componentData?: Record<string, Promise<unknown>>;
    children: React.ReactNode;
}) {
    const existing = useComponentData();
    if (componentData && !existing) {
        return <ComponentDataProvider value={componentData}>{children}</ComponentDataProvider>;
    }
    return <>{children}</>;
}
